import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL } from '../../api/api';

const API_DIRECTOR_URL = `${API_URL}/directors`;

const AdminDirectorLayer = () => {
    const [directors, setDirectors] = useState([]);
    const [formData, setFormData] = useState({
        id: null,
        designation: '',
        directorname: '',
        userfile: null,
        resignationdate: '',
        dinno: '',
        appointmentdate: '',
        authosign: '0',
    });

    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchDirectors();
    }, []);

    const fetchDirectors = async () => {
        try {
            const response = await axios.get(API_DIRECTOR_URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setDirectors(response.data);
        } catch (error) {
            console.error('Failed to fetch directors:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            setFormData({ ...formData, userfile: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = new FormData();

        payload.append('designation', formData.designation);
        payload.append('director_name', formData.directorname);
        payload.append('resignation_date', formData.resignationdate);
        payload.append('appointment_date', formData.appointmentdate);
        payload.append('din_no', formData.dinno);
        payload.append('authorized_signatory', formData.authosign);
        if (formData.userfile) payload.append('signature', formData.userfile);

        try {
            if (formData.id) {
                await axios.post(`${API_DIRECTOR_URL}/${formData.id}`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                Swal.fire('Updated', 'Director updated successfully', 'success');
            } else {
                await axios.post(API_DIRECTOR_URL, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                Swal.fire('Added', 'Director added successfully', 'success');
            }

            setFormData({
                id: null,
                designation: '',
                directorname: '',
                userfile: null,
                resignationdate: '',
                dinno: '',
                appointmentdate: '',
                authosign: '0',
            });
            fetchDirectors();
        } catch (error) {
            console.error('Error saving director:', error);
            Swal.fire('Error', 'Failed to save director', 'error');
        }
    };

    const handleEdit = (director) => {
        setFormData({
            id: director.id,
            designation: director.designation,
            directorname: director.director_name,
            userfile: null,
            resignationdate: director.resignation_date || '',
            dinno: director.din_no || '',
            appointmentdate: director.appointment_date || '',
            authosign: director.authorized_signatory ? '1' : '0',
        });
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will delete the director permanently.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        });

        if (confirm.isConfirmed) {
            try {
                await axios.delete(`${API_DIRECTOR_URL}/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                Swal.fire('Deleted', 'Director has been deleted', 'success');
                fetchDirectors();
            } catch (error) {
                console.error('Error deleting:', error);
                Swal.fire('Error', 'Failed to delete director', 'error');
            }
        }
    };

    return (
        <div className="container mt-4">
            {/* FORM */}
            <div className="card shadow border-0 mb-4">
                <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">{formData.id ? 'Edit' : 'Add'} Director Details</h6>
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit} encType="multipart/form-data">
                        <div className="row g-4">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="designation"
                                        value={formData.designation}
                                        onChange={handleChange}
                                        required
                                        placeholder="Designation"
                                    />
                                    <label>Designation</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="directorname"
                                        value={formData.directorname}
                                        onChange={handleChange}
                                        required
                                        placeholder="Director Name"
                                    />
                                    <label>Director Name</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Signature Upload</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    name="userfile"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="resignationdate"
                                        value={formData.resignationdate}
                                        onChange={handleChange}
                                    />
                                    <label>Resignation Date</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="dinno"
                                        value={formData.dinno}
                                        onChange={handleChange}
                                        placeholder="DIN No"
                                    />
                                    <label>DIN No.</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="appointmentdate"
                                        value={formData.appointmentdate}
                                        onChange={handleChange}
                                    />
                                    <label>Appointment Date</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label d-block">Authorized Signatory</label>
                                <div className="form-check form-check-inline">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="authosign"
                                        value="1"
                                        checked={formData.authosign === '1'}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label">Yes</label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="authosign"
                                        value="0"
                                        checked={formData.authosign === '0'}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label">No</label>
                                </div>
                            </div>
                            <div className="col-12 text-end">
                                <button type="submit" className="btn btn-primary px-4">
                                    {formData.id ? 'Update' : 'Submit'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* TABLE */}
            <div className="card">
                <div className="card-header bg-light">
                    <h6 className="mb-0">Directors List</h6>
                </div>
                <div className="card-body table-responsive">
                    <table className="table table-bordered table-striped">
                        <thead>
                            <tr>
                                <th>Designation</th>
                                <th>Name</th>
                                <th>DIN</th>
                                <th>Appointed</th>
                                <th>Resigned</th>
                                <th>Authorized</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {directors.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center">No records found</td>
                                </tr>
                            ) : (
                                directors.map((dir) => (
                                    <tr key={dir.id}>
                                        <td>{dir.designation}</td>
                                        <td>{dir.director_name}</td>
                                        <td>{dir.din_no}</td>
                                        <td>{dir.appointment_date}</td>
                                        <td>{dir.resignation_date}</td>
                                        <td>{dir.authorized_signatory ? 'Yes' : 'No'}</td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-warning me-2"
                                                onClick={() => handleEdit(dir)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDelete(dir.id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminDirectorLayer;
